<script setup lang="ts">
import {NotificationSequenceDetails} from "@/notification-sequences/interfaces/NotificationSequence";
import {ref} from "vue";
import NotificationSequenceMessage
    from "@/notification-sequences/components/notification-sequence-message/NotificationSequenceMessage.vue";
import {Send} from "@lucide/vue";
import {NotificationTemplate} from "@/notification-template/interfaces";
import dayjs from "dayjs";
import dateHelper from "@/core/helpers/dateHelper";

const props = defineProps<{
    sequence: NotificationSequenceDetails;
    /** Start date of the sequence; when provided, each message shows its estimated reception date. */
    startDate?: string | Date;
}>();

/**
 * Estimated reception date of a message, based on the sequence start date and the message delay (in days).
 *
 * @param delay - The message delay, in days after the sequence start.
 *
 * @return string
 */
const estimatedReceptionDate = (delay: number): string => {
    if (!props.startDate) {
        return '';
    }

    return dateHelper.shortFormat(dayjs(props.startDate).add(delay, 'day').toDate(), 'D MMM');
};

defineSlots<{
    default: (props: { selectedTemplate: NotificationTemplate | undefined }) => unknown;
    "before-messages": () => unknown;
}>();

const selectedMessageIndex = ref<number>(0);
</script>

<template>
  <div v-if="sequence.messages.length" class="flex gap-8 items-center">
    <div class="shrink-0 flex flex-col gap-4 max-w-md">
      <h3 class="text-center">
        Séquence de suivi complet
      </h3>
      <NotificationSequenceMessage
        :show-heading="false"
        :template="sequence.messages[selectedMessageIndex].template"
        :when="''"
      />
    </div>


    <div class="flex flex-col gap-2 sequence-preview-right">
      <slot name="before-messages" />
      <div
        v-for="(message, index) in sequence.messages"
        :key="message.id"
        class="flex items-center gap-4 cursor-pointer border-[1.5px] rounded-lg p-2"
        :class="{
          'border-primary': selectedMessageIndex === index
        }"
        @click="selectedMessageIndex = index"
      >
        <div
          class="sequence-icon shrink-0 z-10"
          :class="{ 'sequence-icon--active': selectedMessageIndex === index }"
        >
          <Send class="w-4 h-4" />
        </div>
        <div>
          <div class="flex items-baseline gap-2">
            <label class="font-medium">
              J+{{ message.delay }}
            </label>
            <span v-if="startDate" class="text-sm text-muted-foreground">
              {{ estimatedReceptionDate(message.delay) }}
            </span>
          </div>
          <p v-if="message.title" class="text-sm text-muted-foreground line-clamp-2">
            {{ message.title ? `${message.title}` : '' }}
          </p>
        </div>
      </div>
      <slot :selected-template="sequence.messages[selectedMessageIndex]?.template" />
    </div>
  </div>
</template>

<style scoped>
.sequence-icon {
    @apply w-10 h-10 rounded-full border-2 border-primary flex items-center justify-center text-primary bg-white ml-[10px] shrink-0;
}

.sequence-icon--active {
    @apply bg-primary text-white;
}
</style>
